import { useState, FormEvent } from 'react';

interface QuoteFormWidgetProps {
  formId: string;
  formUrl: string;
  title?: string;
  description?: string;
  source?: string;
}

export default function QuoteFormWidget({ 
  formId, 
  formUrl, 
  title = "GET A FREE QUOTE",
  description = "Fill out the form below and we'll get back to you within 24 hours",
  source = "Quote Form"
}: QuoteFormWidgetProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    urgency: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const SLACK_WEBHOOK_URL = 'YOUR_SLACK_WEBHOOK_URL';

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const slackPayload = {
        text: `📋 *New Quote Request — ${source}*\n\n*Name:* ${formData.name}\n*Email:* ${formData.email}\n*Phone:* ${formData.phone}\n*Urgency:* ${formData.urgency}\n*Message:* ${formData.message}`,
      };

      const [readyResponse] = await Promise.allSettled([
        fetch(formUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        }),
        fetch(SLACK_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(slackPayload),
        }),
      ]);

      if (readyResponse.status === 'fulfilled' && readyResponse.value.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', urgency: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <section id="get-quote" className="py-12 sm:py-20 bg-[#1a1a1a] border-t-4 border-[#FFD700]">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-4">{title}</h2>
          <p className="text-gray-300 text-base sm:text-lg">
            {description}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-10 shadow-2xl" data-readdy-form id={formId}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            <div>
              <label htmlFor={`${formId}-name`} className="block text-sm font-bold text-[#1a1a1a] mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id={`${formId}-name`}
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label htmlFor={`${formId}-phone`} className="block text-sm font-bold text-[#1a1a1a] mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id={`${formId}-phone`}
                name="phone"
                value={formData.phone}
                onChange={handleFormChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors"
                placeholder="(559) 555-1234"
              />
            </div>
          </div>

          <div className="mb-4 sm:mb-6">
            <label htmlFor={`${formId}-email`} className="block text-sm font-bold text-[#1a1a1a] mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id={`${formId}-email`}
              name="email"
              value={formData.email}
              onChange={handleFormChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors"
              placeholder="john@example.com"
            />
          </div>

          <div className="mb-4 sm:mb-6">
            <label htmlFor={`${formId}-urgency`} className="block text-sm font-bold text-[#1a1a1a] mb-2">
              Urgency *
            </label>
            <select
              id={`${formId}-urgency`}
              name="urgency"
              value={formData.urgency}
              onChange={handleFormChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors bg-white cursor-pointer"
            >
              <option value="">Select urgency level...</option>
              <option value="Emergency — Need help now">🚨 Emergency — Need help now</option>
              <option value="Urgent — Within 24 hours">⚡ Urgent — Within 24 hours</option>
              <option value="Soon — Within a few days">📅 Soon — Within a few days</option>
              <option value="Planning — Just getting a quote">💬 Planning — Just getting a quote</option>
            </select>
          </div>

          <div className="mb-6 sm:mb-8">
            <label htmlFor={`${formId}-message`} className="block text-sm font-bold text-[#1a1a1a] mb-2">
              Message *
            </label>
            <textarea
              id={`${formId}-message`}
              name="message"
              value={formData.message}
              onChange={handleFormChange}
              maxLength={500}
              rows={4}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors resize-none"
              placeholder="Tell us about your electrical needs..."
            ></textarea>
          </div>

          {formStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border-2 border-green-500 rounded-lg flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
              <span className="text-sm font-medium text-green-700">
                Thank you! We'll contact you within 24 hours.
              </span>
            </div>
          )}

          {formStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg flex items-center gap-3">
              <i className="ri-error-warning-fill text-red-500 text-xl"></i>
              <span className="text-sm font-medium text-red-700">
                Something went wrong. Please try again.
              </span>
            </div>
          )}

          <button
            type="submit"
            disabled={formStatus === 'sending'}
            className="w-full bg-[#FFD700] text-[#1a1a1a] py-4 rounded-full font-bold text-sm uppercase tracking-widest hover:bg-[#FFC700] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            {formStatus === 'sending' ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Sending...
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill"></i>
                Get Your Free Quote
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}